import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/bi/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isTenantAdmin } from "@/lib/roles";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Check, Trash2, Loader2, Mail, Send, Newspaper, Sunrise, Users2, Activity, ShieldAlert, ShieldCheck, ShieldX, LogIn, RefreshCw, Download, Monitor, Globe, LogOut, LogIn as LogInIcon, History, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ApiTokensCard } from "@/components/bi/ApiTokensCard";
import { RegionalCard } from "@/components/bi/RegionalCard";
import { AccessibilityCard } from "@/components/bi/AccessibilityCard";
import { ApiSchedulesCard } from "@/components/bi/ApiSchedulesCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AI_MODELS = ["gpt-4o", "gpt-4o-mini", "gpt-4.1", "gpt-4.1-mini", "gpt-5", "gpt-5-mini", "o4-mini"];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [model, setModel] = useState("gpt-4o");
  useEffect(() => { if (user?.ai_model) setModel(user.ai_model); }, [user?.ai_model]);

  const saveModel = async (m) => {
    setModel(m);
    try { await api.post("/auth/ai-model", { model: m }); toast.success(`AI model set to ${m}`); await refreshUser(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to save model"); }
  };

  const save = async () => {
    if (!key.trim()) { toast.error("Enter a key"); return; }
    setSaving(true);
    try {
      await api.post("/auth/openai-key", { openai_key: key.trim() });
      toast.success("OpenAI key saved — AI now uses your key");
      setKey(""); await refreshUser();
    } catch (e) { toast.error("Failed to save key"); } finally { setSaving(false); }
  };
  const remove = async () => {
    setSaving(true);
    try { await api.delete("/auth/openai-key"); toast.success("Removed — back to Emergent key"); await refreshUser(); }
    catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };

  return (
    <AppShell title="Settings">
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-8 space-y-6">
        <Card className="glass-card border-0 p-6">
          <h3 className="font-display text-lg text-[color:var(--ink)]">Profile</h3>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div><Label className="text-[color:var(--ink-3)] text-xs">Name</Label><div className="text-[color:var(--ink)] mt-1">{user?.name}</div></div>
            <div><Label className="text-[color:var(--ink-3)] text-xs">Email</Label><div className="text-[color:var(--ink)] mt-1">{user?.email}</div></div>
          </div>
        </Card>

        <ApiTokensCard />

        <RegionalCard />

        <AccessibilityCard />

        <ApiSchedulesCard />

        <Card className="glass-card border-0 p-6">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[color:var(--brand-ocean)]" />
            <h3 className="font-display text-lg text-[color:var(--ink)]">AI Provider</h3>
            {user?.has_openai_key
              ? <Badge className="bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0">Using your OpenAI key</Badge>
              : <Badge className="bg-[color:rgba(125,183,255,0.18)] text-[color:var(--brand-ocean)] border-0">Using Emergent key</Badge>}
          </div>
          <p className="text-sm text-[color:var(--ink-3)] mt-2">By default AI features use the built-in Emergent key. Add your own OpenAI API key and it will be used for everything instead — the Emergent key is no longer needed.</p>
          <div className="flex gap-2 mt-4">
            <Input data-testid="settings-openai-key" type="password" placeholder="sk-..." value={key} onChange={(e) => setKey(e.target.value)} className="bg-[color:var(--surface)]" />
            <Button data-testid="settings-save-key" onClick={save} disabled={saving} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Save</>}
            </Button>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Label className="text-xs text-[color:var(--ink-3)] whitespace-nowrap">Model</Label>
            <Select value={model} onValueChange={saveModel}>
              <SelectTrigger data-testid="settings-ai-model" className="bg-[color:var(--surface)] w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AI_MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-[11px] text-[color:var(--ink-3)]">Used for Ask AI, insights and digest. Make sure the model is available for your key.</span>
          </div>
          {user?.has_openai_key && (
            <Button data-testid="settings-remove-key" variant="ghost" onClick={remove} className="mt-3 text-[color:var(--danger)] hover:bg-[color:rgba(232,93,106,0.08)]"><Trash2 className="w-4 h-4 mr-1" />Remove my key</Button>
          )}
        </Card>

        <DigestCard />

        <BriefingCard />
        <MySessionsCard />
        {isTenantAdmin(user?.role) && <HealthDigestCard />}

        {isTenantAdmin(user?.role) && <SmtpCard />}
        {isTenantAdmin(user?.role) && <WebhookChannelsCard />}
        {isTenantAdmin(user?.role) && <DeliveryLogCard />}
        {isTenantAdmin(user?.role) && <RotateSessionsCard />}
        {isTenantAdmin(user?.role) && <LoginActivityCard />}
      </div>
    </AppShell>
  );
}

function MySessionsCard() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load sessions"); setItems([]); }
  };
  useEffect(() => { load(); }, []);
  const revoke = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(`Signed out ${data.revoked_count} other session${data.revoked_count === 1 ? "" : "s"}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to revoke sessions"); }
    finally { setBusy(false); }
  };
  const parseUA = (ua) => {
    if (!ua) return { device: "Unknown", browser: "" };
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
    let browser = "Browser";
    if (/Firefox/i.test(ua)) browser = "Firefox";
    else if (/Edg/i.test(ua)) browser = "Edge";
    else if (/Chrome/i.test(ua)) browser = "Chrome";
    else if (/Safari/i.test(ua)) browser = "Safari";
    else if (/curl/i.test(ua)) browser = "cURL";
    let os = "";
    if (/Windows/i.test(ua)) os = "Windows";
    else if (/Mac OS/i.test(ua)) os = "macOS";
    else if (/Linux/i.test(ua)) os = "Linux";
    else if (/Android/i.test(ua)) os = "Android";
    else if (/iPhone|iPad/i.test(ua)) os = "iOS";
    return { device: isMobile ? "Mobile" : "Desktop", browser: `${browser}${os ? ` · ${os}` : ""}` };
  };
  const otherCount = (items || []).filter((s) => !s.current).length;

  return (
    <Card data-testid="my-sessions-card" className="bg-[color:var(--surface)]/85 border border-[color:var(--glass-border)] p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-[color:rgba(43,78,255,0.10)] flex items-center justify-center shrink-0">
          <Monitor className="w-5 h-5 text-[color:var(--brand-ocean)]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[color:var(--ink)]">My Active Sessions</div>
          <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5">
            Every device you&apos;re signed in on. Sign out any device you don&apos;t recognise.
          </div>
        </div>
        {otherCount > 0 && (
          <Button data-testid="revoke-others-btn" size="sm" variant="outline" onClick={revoke} disabled={busy}
                  className="border-[color:var(--danger)]/40 text-[color:var(--danger)] hover:bg-[color:rgba(232,93,106,0.08)]">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <LogOut className="w-3.5 h-3.5 mr-1" />}
            Sign out other devices
          </Button>
        )}
      </div>
      <div className="space-y-2" data-testid="my-sessions-list">
        {!items && <div className="text-[color:var(--ink-3)] text-sm py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
        {items && items.length === 0 && <div className="text-[color:var(--ink-3)] text-sm py-4">No active sessions.</div>}
        {items && items.map((s) => {
          const ua = parseUA(s.user_agent);
          return (
            <div key={s.id} data-testid={s.current ? "session-current" : "session-other"}
                 className={`flex items-center gap-3 p-3 rounded-xl border ${s.current ? "border-[color:var(--brand-ocean)]/30 bg-[color:rgba(43,78,255,0.04)]" : "border-[color:var(--glass-border)]"}`}>
              <div className="w-9 h-9 rounded-lg bg-[color:var(--surface-2)] flex items-center justify-center shrink-0">
                <Monitor className="w-4 h-4 text-[color:var(--ink-2)]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm text-[color:var(--ink)] font-medium">
                  {ua.device} · {ua.browser}
                  {s.current && <Badge className="bg-[color:var(--brand-ocean)] text-white border-0 text-[10px]">This device</Badge>}
                </div>
                <div className="text-[11px] text-[color:var(--ink-3)] font-mono mt-0.5">
                  {s.ip || "unknown IP"} · last seen {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "—"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <SessionsTimeline />
    </Card>
  );
}

/**
 * Renders a compact vertical timeline of the user's (or an admin-selected
 * user's) recent sign-in/out events. Merges login_attempts (successes /
 * failures / lockouts) with expired user_sessions rows to read as a story
 * rather than a raw log. Exposed on the My Sessions card AND embedded in
 * the admin Sessions dialog on the Team page (via the `userId` prop).
 */
export function SessionsTimeline({ userId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const params = userId ? { user_id: userId, limit: 25 } : { limit: 25 };
    api.get("/auth/sessions/timeline", { params })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [userId]);
  const relative = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
    return d.toLocaleDateString();
  };
  const meta = {
    signed_in: { icon: LogInIcon, label: "Signed in", color: "text-[color:var(--success)]", bg: "bg-[color:rgba(0,194,168,0.12)]" },
    signed_out: { icon: LogOut, label: "Signed out", color: "text-[color:var(--ink-2)]", bg: "bg-[color:var(--surface-2)]" },
    sign_in_failed: { icon: ShieldX, label: "Failed sign-in", color: "text-[color:var(--danger)]", bg: "bg-[color:rgba(232,93,106,0.10)]" },
    locked_out: { icon: ShieldAlert, label: "Account locked", color: "text-[color:var(--warning,#eab308)]", bg: "bg-[color:rgba(234,179,8,0.12)]" },
  };
  const parseUA = (ua) => {
    if (!ua) return "Unknown client";
    let b = null;
    if (/Firefox/i.test(ua)) b = "Firefox";
    else if (/Edg/i.test(ua)) b = "Edge";
    else if (/Chrome/i.test(ua)) b = "Chrome";
    else if (/Safari/i.test(ua)) b = "Safari";
    else if (/curl/i.test(ua)) b = "cURL";
    else if (/python|axios|node-fetch|okhttp/i.test(ua)) b = "API client";
    return b || (ua.length > 40 ? ua.slice(0, 40) + "…" : ua);
  };
  return (
    <div className="mt-5 pt-5 border-t border-[color:var(--glass-border)]" data-testid="sessions-timeline">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-[color:var(--ink-3)]" />
        <div className="text-xs font-medium text-[color:var(--ink-2)]">Recent activity</div>
      </div>
      {!items && <div className="text-[color:var(--ink-3)] text-xs py-3"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" />Loading timeline…</div>}
      {items && items.length === 0 && <div className="text-[color:var(--ink-3)] text-xs py-3">No activity yet.</div>}
      {items && items.length > 0 && (
        <ol className="relative border-l border-[color:var(--glass-border)] ml-2 space-y-3">
          {items.map((ev, idx) => {
            const m = meta[ev.type] || meta.sign_in_failed;
            const Icon = m.icon;
            const where = ev.country_name || (ev.country_code === "LAN" ? "local network" : ev.ip || "unknown IP");
            return (
              <li key={`${ev.type}-${ev.at}-${idx}`} data-testid={`timeline-event-${ev.type}`} className="ml-4">
                <span className={`absolute -left-[9px] w-[18px] h-[18px] rounded-full ${m.bg} flex items-center justify-center ring-2 ring-[color:var(--surface)]`}>
                  <Icon className={`w-2.5 h-2.5 ${m.color}`} />
                </span>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className={`text-[12px] font-medium ${m.color}`}>{m.label}</span>
                  <span className="text-[11px] text-[color:var(--ink-3)]">from {where}</span>
                  <span className="text-[10px] text-[color:var(--ink-3)] ml-auto"><Clock className="w-2.5 h-2.5 inline mr-0.5" />{relative(ev.at)}</span>
                </div>
                <div className="text-[11px] text-[color:var(--ink-3)] mt-0.5">{parseUA(ev.user_agent)}</div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function LoginActivityCard() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { outcome: filter };
      const { data } = await api.get("/auth/login-activity", { params: { ...params, limit: 100 } });
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load activity"); setItems([]); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const outcomeMeta = {
    success: { icon: ShieldCheck, color: "text-[color:var(--success)]", bg: "bg-[color:rgba(0,194,168,0.10)]", label: "Success" },
    failure: { icon: ShieldX, color: "text-[color:var(--danger)]", bg: "bg-[color:rgba(232,93,106,0.10)]", label: "Failed" },
    locked:  { icon: ShieldAlert, color: "text-[color:var(--warning,#eab308)]", bg: "bg-[color:rgba(234,179,8,0.10)]", label: "Locked" },
  };

  return (
    <Card data-testid="login-activity-card" className="bg-[color:var(--surface)]/85 border border-[color:var(--glass-border)] p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[color:rgba(43,78,255,0.10)] flex items-center justify-center shrink-0">
            <LogIn className="w-5 h-5 text-[color:var(--brand-ocean)]" />
          </div>
          <div>
            <div className="font-medium text-[color:var(--ink)]">Login Activity</div>
            <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5">
              Recent sign-in attempts (last 30 days). Spot credential-stuffing early — repeated
              failures or lockouts against the same account are a red flag.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <a data-testid="login-activity-export"
             href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/auth/login-activity/export.csv`}
             className="w-9 h-9 rounded-xl flex items-center justify-center text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] transition-colors"
             title="Download last 30 days as CSV">
            <Download className="w-[16px] h-[16px]" />
          </a>
          <button data-testid="login-activity-refresh" onClick={load} disabled={loading}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] transition-colors disabled:opacity-50">
            <RefreshCw className={`w-[16px] h-[16px] ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        {["all", "success", "failure", "locked"].map((f) => (
          <button
            key={f}
            data-testid={`login-activity-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
              filter === f
                ? "bg-[color:var(--brand-ocean)] text-white border-[color:var(--brand-ocean)]"
                : "border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]"
            }`}
          >{f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase font-semibold tracking-wider text-[color:var(--ink-3)]">
              <th className="py-2 pr-3">When</th>
              <th className="py-2 pr-3">Account</th>
              <th className="py-2 pr-3">Outcome</th>
              <th className="py-2 pr-3">Location</th>
              <th className="py-2 pr-3">IP</th>
              <th className="py-2">Client</th>
            </tr>
          </thead>
          <tbody data-testid="login-activity-rows">
            {loading && !items && (
              <tr><td colSpan="6" className="py-6 text-center text-[color:var(--ink-3)]"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></td></tr>
            )}
            {items && items.length === 0 && (
              <tr><td colSpan="6" className="py-6 text-center text-[color:var(--ink-3)]">No login activity yet.</td></tr>
            )}
            {items && items.map((row) => {
              const meta = outcomeMeta[row.outcome] || outcomeMeta.failure;
              const Icon = meta.icon;
              const when = row.created_at ? new Date(row.created_at).toLocaleString() : "—";
              const ua = (row.user_agent || "").split(" ").slice(0, 3).join(" ") || "—";
              const country = row.country_name || (row.country_code === "LAN" ? "Private" : "—");
              return (
                <tr key={row.id} data-testid={`login-activity-row-${row.outcome}`} className="border-t border-[color:var(--glass-border)]">
                  <td className="py-2 pr-3 whitespace-nowrap text-[color:var(--ink-2)]">{when}</td>
                  <td className="py-2 pr-3 font-mono text-[12px] text-[color:var(--ink)]">{row.email_masked}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.bg} ${meta.color}`}>
                      <Icon className="w-3 h-3" />{meta.label}
                      {row.outcome === "locked" && row.retry_after ? ` · ${Math.ceil(row.retry_after / 60)}m` : ""}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-[11px] text-[color:var(--ink-2)] whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      <Globe className="w-3 h-3 text-[color:var(--ink-3)]" />{country === "Private Network" ? "Private" : country}
                    </span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-[color:var(--ink-3)]">{row.ip || "—"}</td>
                  <td className="py-2 text-[11px] text-[color:var(--ink-3)] truncate max-w-[220px]" title={row.user_agent}>{ua}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RotateSessionsCard() {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const rotate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/security/rotate-sessions");
      setLastResult(data);
      toast.success(`Signed out ${data.users_rotated} user${data.users_rotated === 1 ? "" : "s"}. Your session was refreshed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rotate sessions");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="rotate-sessions-card" className="bg-[color:var(--surface)]/85 border border-[color:var(--glass-border)] p-6">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[color:rgba(232,93,106,0.10)] flex items-center justify-center shrink-0">
          <ShieldAlert className="w-5 h-5 text-[color:var(--danger)]" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-[color:var(--ink)]">Force-logout all users</div>
          <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5">
            Invalidates every active session in this tenant. Use after a suspected credential leak or when an
            administrator leaves. Your own session is refreshed automatically so you stay signed in.
          </div>
        </div>
      </div>
      {lastResult && (
        <div data-testid="rotate-sessions-last" className="text-[11px] text-[color:var(--ink-3)] mb-3">
          Last run: signed out <span className="font-medium text-[color:var(--ink)]">{lastResult.users_rotated}</span> user(s) ·
          new session version <span className="font-mono text-[color:var(--ink)]">{lastResult.next_session_version}</span>
        </div>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button data-testid="rotate-sessions-btn" variant="outline"
                  className="border-[color:var(--danger)]/40 text-[color:var(--danger)] hover:bg-[color:rgba(232,93,106,0.08)]">
            <ShieldAlert className="w-4 h-4 mr-2" />Force-logout everyone
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="rotate-sessions-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Force-logout every user in this tenant?</AlertDialogTitle>
            <AlertDialogDescription>
              All current sessions become invalid immediately. Anyone signed in right now will be redirected to
              the login screen on their next request. This action is audit-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rotate-sessions-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="rotate-sessions-confirm-btn" onClick={rotate} disabled={busy}
                               className="bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Rotating…</> : "Yes, rotate sessions"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function DigestCard() {
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(8);
  const [lastSent, setLastSent] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/settings/digest").then(({ data }) => { setEnabled(data.enabled); setHour(data.hour_utc); setLastSent(data.last_sent_date); }).catch(() => {});
  }, []);

  const save = async (en, h) => {
    setEnabled(en); setHour(h);
    try { await api.put("/settings/digest", { enabled: en, hour_utc: Number(h) }); toast.success("Digest preferences saved"); }
    catch { toast.error("Failed to save"); }
  };
  const sendNow = async () => {
    setSending(true);
    try { const { data } = await api.post("/settings/digest/send-now"); toast.success(`Digest sent (${data.detail})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  return (
    <Card className="glass-card border-0 p-6">
      <div className="flex items-center gap-2">
        <Newspaper className="w-5 h-5 text-[color:var(--brand-ocean)]" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Daily digest</h3>
        {enabled && <Badge className="bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0">On</Badge>}
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">One summary per day of your breached/recovered alerts and KPI movements over the last 24h — in-app, plus email if your workspace has SMTP configured.</p>
      <div className="flex items-center gap-4 mt-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="digest-toggle" checked={enabled} onCheckedChange={(v) => save(v, hour)} />Enable daily digest</label>
        {enabled && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[color:var(--ink-3)] text-xs">Deliver at (UTC)</span>
            <Select value={String(hour)} onValueChange={(v) => save(enabled, v)}>
              <SelectTrigger data-testid="digest-hour" className="w-[90px] bg-[color:var(--surface)] h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: 24 }, (_, i) => <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <Button data-testid="digest-send-now" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={sendNow} disabled={sending}>{sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send now</Button>
        {lastSent && <span className="text-xs text-[color:var(--ink-3)]">Last sent: {lastSent}</span>}
      </div>
    </Card>
  );
}

function BriefingCard() {
  const { user } = useAuth();
  const isAdmin = isTenantAdmin(user?.role);
  const [f, setF] = useState({ enabled: false, hour_utc: 8 });
  const [sending, setSending] = useState(false);
  const [teamSending, setTeamSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.get("/settings/briefing").then(({ data }) => setF({ enabled: !!data.enabled, hour_utc: data.hour_utc ?? 8 })).catch(() => {});
  }, []);

  const save = async (patch) => {
    const next = { ...f, ...patch };
    setF(next);
    try { await api.put("/settings/briefing", next); toast.success("Monday briefing settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const sendNow = async () => {
    setSending(true);
    try { const { data } = await api.post("/settings/briefing/send-now"); toast.success(`Briefing sent (${data.detail})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  const sendToTeam = async () => {
    if (!window.confirm("Send this week's briefing to every opted-in teammate? Each brief is built with THEIR permissions so RLS/CLS is preserved.")) return;
    setTeamSending(true);
    try {
      const { data } = await api.post("/settings/briefing/send-tenant", { audience: "opted-in" });
      toast.success(`Team briefing: ${data.sent}/${data.recipients} sent, ${data.skipped} skipped, ${data.failed} failed`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Team send failed"); }
    finally { setTeamSending(false); }
  };
  const doPreview = async () => {
    setPreviewing(true); setPreview(null);
    try { const { data } = await api.post("/settings/briefing/preview"); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  return (
    <Card className="glass-card border-0 p-6" data-testid="briefing-card">
      <div className="flex items-center gap-2">
        <Sunrise className="w-5 h-5 text-[color:var(--brand-mint)]" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Monday briefing</h3>
        {f.enabled && <Badge className="bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0">On</Badge>}
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">An AI-written executive summary every Monday: week-over-week KPI movement across your modeled datasets, next-week forecasts, and alert health — in-app and by branded email.</p>
      <div className="flex items-center gap-5 mt-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
          <Switch data-testid="briefing-toggle" checked={f.enabled} onCheckedChange={(v) => save({ enabled: v })} />Send every Monday
        </label>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[color:var(--ink-3)]">Hour (UTC)</Label>
          <Input data-testid="briefing-hour" type="number" min={0} max={23} className="w-20 h-8 bg-[color:var(--surface)]" value={f.hour_utc}
            onChange={(e) => setF((x) => ({ ...x, hour_utc: Number(e.target.value) || 0 }))} onBlur={() => save({})} />
        </div>
        <span className="flex-1" />
        <Button data-testid="briefing-preview-btn" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={doPreview} disabled={previewing}>
          {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Newspaper className="w-4 h-4 mr-1" />}Preview
        </Button>
        <Button data-testid="briefing-send-btn" size="sm" onClick={sendNow} disabled={sending} className="bg-[color:var(--brand-mint)] hover:brightness-95 text-white">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send now
        </Button>
        {isAdmin && (
          <Button data-testid="briefing-send-team-btn" size="sm" onClick={sendToTeam} disabled={teamSending} className="bg-[color:var(--brand-ocean)] hover:brightness-95 text-white">
            {teamSending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Users2 className="w-4 h-4 mr-1" />}Send to team
          </Button>
        )}
      </div>
      {isAdmin && (
        <p className="mt-2 text-[11px] text-[color:var(--ink-3)]"><Users2 className="inline w-3 h-3 mr-1" />Team send delivers a personalized brief to every teammate who has enabled Monday briefing — each one is built with THEIR identity so row / column security is preserved.</p>
      )}
      {preview && (
        <div className="mt-4 rounded-xl border border-[color:var(--glass-border)] overflow-hidden" data-testid="briefing-preview">
          <div className="px-3 py-2 text-xs font-semibold text-[color:var(--ink-2)] bg-[color:var(--surface-2)] border-b border-[color:var(--glass-border)]">{preview.subject}</div>
          <iframe title="briefing preview" srcDoc={preview.html} className="w-full h-80 bg-white" sandbox="" />
        </div>
      )}
    </Card>
  );
}

// Weekly Platform Health Digest (admins): top failing dashboards + slowest queries.
function HealthDigestCard() {
  const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [f, setF] = useState({ enabled: false, weekday: 0, hour_utc: 8 });
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.get("/settings/health-digest").then(({ data }) => setF({ enabled: !!data.enabled, weekday: data.weekday ?? 0, hour_utc: data.hour_utc ?? 8 })).catch(() => {});
  }, []);

  const save = async (patch) => {
    const next = { ...f, ...patch };
    setF(next);
    try { await api.put("/settings/health-digest", next); toast.success("Health digest settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const sendNow = async () => {
    setSending(true);
    try { const { data } = await api.post("/settings/health-digest/send-now"); toast.success(`Digest sent (${data.detail})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  const doPreview = async () => {
    setPreviewing(true); setPreview(null);
    try { const { data } = await api.post("/settings/health-digest/preview"); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  return (
    <Card className="glass-card border-0 p-6" data-testid="health-digest-card">
      <div className="flex items-center gap-2">
        <Activity className="w-5 h-5 text-[color:var(--brand-ocean)]" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Platform health digest</h3>
        {f.enabled && <Badge className="bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0">On</Badge>}
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">A weekly admin report: top failing dashboards, error volume, slowest datasets, p95 query time and cache hit rate for the last 7 days — in-app and by email.</p>
      <div className="flex items-center gap-4 mt-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
          <Switch data-testid="health-digest-toggle" checked={f.enabled} onCheckedChange={(v) => save({ enabled: v })} />Send weekly
        </label>
        <Select value={String(f.weekday)} onValueChange={(v) => save({ weekday: Number(v) })}>
          <SelectTrigger data-testid="health-digest-weekday" className="w-[130px] h-8 bg-[color:var(--surface)] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[color:var(--ink-3)]">Hour (UTC)</Label>
          <Input data-testid="health-digest-hour" type="number" min={0} max={23} className="w-20 h-8 bg-[color:var(--surface)]" value={f.hour_utc}
            onChange={(e) => setF((x) => ({ ...x, hour_utc: Number(e.target.value) || 0 }))} onBlur={() => save({})} />
        </div>
        <span className="flex-1" />
        <Button data-testid="health-digest-preview-btn" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={doPreview} disabled={previewing}>
          {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Newspaper className="w-4 h-4 mr-1" />}Preview
        </Button>
        <Button data-testid="health-digest-send-btn" size="sm" onClick={sendNow} disabled={sending} className="bg-[color:var(--brand-ocean)] hover:brightness-95 text-white">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send now
        </Button>
      </div>
      {preview && (
        <div className="mt-4 rounded-xl border border-[color:var(--glass-border)] overflow-hidden" data-testid="health-digest-preview">
          <div className="px-3 py-2 text-xs font-semibold text-[color:var(--ink-2)] bg-[color:var(--surface-2)] border-b border-[color:var(--glass-border)]">{preview.subject}</div>
          <iframe title="health digest preview" srcDoc={preview.html} className="w-full h-80 bg-white" sandbox="" />
        </div>
      )}
    </Card>
  );
}

function SmtpCard() {
  const METHODS = {
    smtp: { label: "SMTP server" },
    sendgrid_api: { label: "SendGrid API", hint: "Create a key with Mail Send permission at SendGrid › Settings › API Keys (SG.xxx…). The From address must be a verified sender." },
    resend_api: { label: "Resend API", hint: "Create an API key at resend.com › API Keys (re_xxx…). The From address must belong to a verified domain (or use onboarding@resend.dev for testing)." },
  };
  const PRESETS = {
    custom:    { label: "Custom / other" },
    gmail:     { label: "Gmail",              host: "smtp.gmail.com",   port: 587, security: "starttls",
                 hint: "Enable 2FA on the account, then create an App Password at myaccount.google.com/apppasswords and paste it below." },
    outlook:   { label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587, security: "starttls",
                 hint: "Use your full mailbox address as username. Modern-auth accounts may need an app password from account.microsoft.com." },
    sendgrid:  { label: "SendGrid",           host: "smtp.sendgrid.net", port: 587, security: "starttls",
                 hint: "Username is literally 'apikey' and the password is your SendGrid API key (SG.xxx…)." },
    mailgun:   { label: "Mailgun",            host: "smtp.mailgun.org",  port: 587, security: "starttls",
                 hint: "Get SMTP credentials from Mailgun › Domains › SMTP credentials." },
    ses:       { label: "Amazon SES",         host: "email-smtp.us-east-1.amazonaws.com", port: 587, security: "starttls",
                 hint: "Create SMTP credentials in SES › SMTP Settings. The username is different from your AWS IAM access key." },
    postmark:  { label: "Postmark",           host: "smtp.postmarkapp.com", port: 587, security: "starttls",
                 hint: "Username and password are both your Server API Token from Postmark." },
    resend:    { label: "Resend",             host: "smtp.resend.com",   port: 465, security: "ssl",
                 hint: "Username is 'resend' and the password is your Resend API key (re_xxx)." },
    zoho:      { label: "Zoho Mail",          host: "smtp.zoho.com",     port: 465, security: "ssl",
                 hint: "Use your full Zoho mailbox address as username." },
  };
  const [f, setF] = useState({ provider: "smtp", host: "", port: 587, username: "", password: "", api_key: "", from_email: "", security: "starttls" });
  const [preset, setPreset] = useState("custom");
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);   // {ok, message}
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("/settings/smtp").then(({ data }) => {
      if (data.configured) {
        setConfigured(true);
        setF((x) => ({ ...x, provider: data.provider || "smtp", host: data.host || "", port: data.port || 587, username: data.username || "", from_email: data.from_email || "", security: data.security || "starttls" }));
        // Match existing host to a preset if possible
        const match = Object.entries(PRESETS).find(([, p]) => p.host && p.host === data.host);
        if (match) setPreset(match[0]);
      }
    }).catch(() => {});
    // PRESETS is a module constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS[key];
    if (!p || !p.host) return;
    setF((x) => ({ ...x, host: p.host, port: p.port, security: p.security }));
    setTestResult(null);
  };

  const save = async () => {
    const isApi = f.provider !== "smtp";
    if (!f.from_email || (!isApi && !f.host)) { toast.error(isApi ? "From email is required" : "Host and From email are required"); return; }
    setSaving(true);
    try {
      await api.put("/settings/smtp", { ...f, port: Number(f.port) || 587, password: f.password || null, api_key: f.api_key || null });
      toast.success("Email delivery settings saved");
      setConfigured(true); set("password", ""); set("api_key", "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/settings/smtp/test", {});
      setTestResult({ ok: true, message: `Test email sent to ${data.sent_to}. Check your inbox to confirm delivery.` });
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "SMTP test failed";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    }
    finally { setTesting(false); }
  };

  const presetHint = PRESETS[preset]?.hint;

  return (
    <Card className="glass-card border-0 p-6">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-[color:var(--brand-ocean)]" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Email delivery (SMTP)</h3>
        {configured
          ? <Badge className="bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0">Configured</Badge>
          : <Badge variant="outline" className="text-[color:var(--ink-3)]">Not configured</Badge>}
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">Used to email alert notifications, scheduled reports and snapshot alerts. Use your SMTP server, or a SendGrid/Resend API key if SMTP ports are blocked.</p>

      {/* Delivery method */}
      <div className="mt-4">
        <Label className="text-xs">Delivery method</Label>
        <Select value={f.provider} onValueChange={(v) => { set("provider", v); setTestResult(null); }}>
          <SelectTrigger data-testid="email-provider" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(METHODS).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {METHODS[f.provider]?.hint && (
          <div data-testid="email-provider-hint" className="mt-2 text-[11px] text-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/8 border border-[color:var(--brand-ocean)]/20 rounded-lg px-3 py-2">
            {METHODS[f.provider].hint}
          </div>
        )}
      </div>

      {f.provider !== "smtp" ? (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div><Label className="text-xs">From email</Label><Input data-testid="email-api-from" className="mt-1.5 bg-[color:var(--surface)]" placeholder="reports@yourco.com" value={f.from_email} onChange={(e) => set("from_email", e.target.value)} /></div>
          <div><Label className="text-xs">API key {configured && "(leave blank to keep)"}</Label><Input data-testid="email-api-key" type="password" className="mt-1.5 bg-[color:var(--surface)]" placeholder={f.provider === "sendgrid_api" ? "SG.xxxx…" : "re_xxxx…"} value={f.api_key} onChange={(e) => set("api_key", e.target.value)} /></div>
        </div>
      ) : (
      <>
      {/* Provider preset picker */}
      <div className="mt-4">
        <Label className="text-xs">Provider preset</Label>
        <Select value={preset} onValueChange={applyPreset}>
          <SelectTrigger data-testid="smtp-preset" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(PRESETS).map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {presetHint && (
          <div data-testid="smtp-preset-hint" className="mt-2 text-[11px] text-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/8 border border-[color:var(--brand-ocean)]/20 rounded-lg px-3 py-2">
            {presetHint}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4">
        <div className="col-span-2"><Label className="text-xs">SMTP host</Label><Input data-testid="smtp-host" className="mt-1.5 bg-[color:var(--surface)]" placeholder="smtp.example.com" value={f.host} onChange={(e) => set("host", e.target.value)} /></div>
        <div><Label className="text-xs">Port</Label><Input data-testid="smtp-port" type="number" className="mt-1.5 bg-[color:var(--surface)]" value={f.port} onChange={(e) => set("port", e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div><Label className="text-xs">Username</Label><Input data-testid="smtp-username" className="mt-1.5 bg-[color:var(--surface)]" value={f.username} onChange={(e) => set("username", e.target.value)} /></div>
        <div><Label className="text-xs">Password {configured && "(leave blank to keep)"}</Label><Input data-testid="smtp-password" type="password" className="mt-1.5 bg-[color:var(--surface)]" value={f.password} onChange={(e) => set("password", e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div><Label className="text-xs">From email</Label><Input data-testid="smtp-from" className="mt-1.5 bg-[color:var(--surface)]" placeholder="reports@yourco.com" value={f.from_email} onChange={(e) => set("from_email", e.target.value)} /></div>
        <div><Label className="text-xs">Security</Label>
          <Select value={f.security} onValueChange={(v) => set("security", v)}>
            <SelectTrigger data-testid="smtp-security" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starttls">STARTTLS (port 587)</SelectItem>
              <SelectItem value="ssl">SSL/TLS (port 465)</SelectItem>
              <SelectItem value="none">None (not recommended)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      </>
      )}
      <div className="flex gap-2 mt-4">
        <Button data-testid="smtp-save" onClick={save} disabled={saving} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Save settings</Button>
        <Button data-testid="smtp-test" variant="outline" onClick={test} disabled={testing || !configured} className="bg-[color:var(--surface)]">{testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send test email</Button>
      </div>

      {/* Test result banner */}
      {testResult && (
        <div data-testid="smtp-test-result"
          className={`mt-3 text-xs px-3 py-2 rounded-lg border ${testResult.ok
            ? "bg-[color:rgba(0,194,168,0.10)] border-[color:var(--success)]/30 text-[color:var(--success)]"
            : "bg-[color:rgba(239,68,68,0.08)] border-[color:var(--danger)]/30 text-[color:var(--danger)]"}`}>
          {testResult.ok ? "✓ " : "✗ "}{testResult.message}
        </div>
      )}
    </Card>
  );
}

// Slack / Microsoft Teams incoming-webhook channels for alerts & report notifications.
function WebhookChannelsCard() {
  const [cfg, setCfg] = useState({ slack_enabled: false, teams_enabled: false, slack_configured: false, teams_configured: false });
  const [urls, setUrls] = useState({ slack_url: "", teams_url: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");
  const load = () => api.get("/settings/channels").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/channels", {
        slack_enabled: cfg.slack_enabled, teams_enabled: cfg.teams_enabled,
        slack_url: urls.slack_url || null, teams_url: urls.teams_url || null,
      });
      toast.success("Notification channels saved");
      setUrls({ slack_url: "", teams_url: "" });
      setCfg((c) => ({ ...c, slack_configured: data.slack_configured, teams_configured: data.teams_configured }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const test = async (channel) => {
    setTesting(channel);
    try { await api.post("/settings/channels/test", { channel }); toast.success(`Test message sent to ${channel}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(""); }
  };
  const Row = ({ ch, label, placeholder }) => (
    <div className="mt-4 first:mt-0">
      <div className="flex items-center gap-2">
        <Switch data-testid={`channel-${ch}-enabled`} checked={cfg[`${ch}_enabled`]} onCheckedChange={(v) => setCfg((c) => ({ ...c, [`${ch}_enabled`]: v }))} />
        <span className="text-sm font-medium text-[color:var(--ink)]">{label}</span>
        {cfg[`${ch}_configured`]
          ? <Badge className="bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0">URL saved</Badge>
          : <Badge variant="outline" className="text-[color:var(--ink-3)]">No URL</Badge>}
        <span className="flex-1" />
        <Button data-testid={`channel-${ch}-test`} size="sm" variant="outline" className="bg-[color:var(--surface)] h-7 text-xs"
          disabled={!cfg[`${ch}_configured`] || testing === ch} onClick={() => test(ch)}>
          {testing === ch ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}Test
        </Button>
      </div>
      <Input data-testid={`channel-${ch}-url`} type="password" className="mt-2 bg-[color:var(--surface)]"
        placeholder={cfg[`${ch}_configured`] ? "•••••• (leave blank to keep)" : placeholder}
        value={urls[`${ch}_url`]} onChange={(e) => setUrls((u) => ({ ...u, [`${ch}_url`]: e.target.value }))} />
    </div>
  );
  return (
    <Card className="glass-card border-0 p-6">
      <div className="flex items-center gap-2">
        <Send className="w-5 h-5 text-[color:var(--brand-mint)]" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Slack & Teams notifications</h3>
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">Alerts can post straight into a channel. Create an incoming webhook in Slack (api.slack.com/messaging/webhooks) or Teams (channel &gt; Connectors &gt; Incoming Webhook) and paste the URL.</p>
      <Row ch="slack" label="Slack" placeholder="https://hooks.slack.com/services/…" />
      <Row ch="teams" label="Microsoft Teams" placeholder="https://outlook.office.com/webhook/…" />
      <Button data-testid="channels-save" onClick={save} disabled={saving} className="mt-4 bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Save channels
      </Button>
    </Card>
  );
}

// Recent delivery attempts (email + webhooks) with status — the reliability view.
function DeliveryLogCard() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (open) api.get("/settings/delivery-log").then(({ data }) => setItems(data)).catch(() => {}); }, [open]);
  return (
    <Card className="glass-card border-0 p-6">
      <div className="flex items-center gap-2">
        <Activity className="w-5 h-5 text-[color:var(--brand-ocean)]" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Delivery log</h3>
        <span className="flex-1" />
        <Button data-testid="delivery-log-toggle" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={() => setOpen(!open)}>{open ? "Hide" : "Show"}</Button>
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">Every alert, report and digest delivery attempt — so you can see exactly what went out and what failed.</p>
      {open && (
        <div className="mt-3 max-h-72 overflow-auto rounded-xl border border-[color:var(--glass-border)]" data-testid="delivery-log-list">
          {items.length === 0 && <div className="p-6 text-center text-xs text-[color:var(--ink-3)]">No deliveries yet.</div>}
          {items.map((d) => (
            <div key={d.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-[color:var(--glass-border)] last:border-0 text-xs">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${d.status === "sent" ? "bg-[color:var(--success)]" : "bg-[color:var(--danger)]"}`} />
              <span className="text-[color:var(--ink-2)] font-medium">{d.channel}</span>
              <span className="text-[color:var(--ink-3)] truncate flex-1">{d.subject || d.target}</span>
              <Badge variant="outline" className="text-[9px] text-[color:var(--ink-3)]">{d.source}</Badge>
              {d.status !== "sent" && <span className="text-[color:var(--danger)] truncate max-w-[200px]" title={d.error}>{d.error}</span>}
              <span className="text-[color:var(--ink-3)] whitespace-nowrap">{new Date(d.ts).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
